import { Spinner } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DIMENSION_BY_KEY, MEASURE_BY_KEY, ROOT_KEY } from './constants';
import {
  GRAND_TOTAL_BG,
  HEADER_BG,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  LINE_GROUP,
  LINE_STRONG,
  LINK,
  PRIMARY,
  ROW_BG,
  ROW_HOVER,
  SUBTOTAL_BG,
} from './pivotStyles';

/*
 * Tabla dinámica del informe de existencias. Pinta el árbol que devuelve
 * el back (no recalcula nada):
 *
 *   - Columnas: `pivot.columns` (árbol con `leafCount`). Se aplana en una
 *     lista de columnas de datos: cada hoja, y tras los hijos de cada grupo
 *     intermedio su columna de subtotal ("Total <grupo>"). TOTAL va aparte,
 *     al final, y puede quedarse fija a la derecha.
 *   - Cada columna de datos se abre en las medidas activas, y cada medida en
 *     (actual, comparación, Δ %) si hay comparativa.
 *   - Filas: `pivot.rows` (árbol). El subtotal va en la fila del nodo; los
 *     hijos se despliegan debajo. TOTAL (la raíz) al final.
 *
 * Anchos de columna fijos y conocidos de antemano (table-layout: fixed):
 * así las celdas fijas a la derecha saben su `right` sin medir el DOM, y la
 * barra "Ir a" sabe dónde empieza cada grupo.
 *
 * Sin altura máxima: la tabla ocupa lo que necesita y es la página la que
 * se desplaza en vertical. Como un contenedor con `overflow-x: auto` es
 * también el contenedor de los sticky verticales, la cabecera no puede
 * vivir en él: va en una tabla aparte (mismo `colgroup`, mismos anchos)
 * pegada al viewport bajo la barra superior, con su `scrollLeft`
 * sincronizado con el del cuerpo. La barra de desplazamiento horizontal
 * también es aparte y se pega al pie de la ventana, para no tener que bajar
 * al final de la tabla para moverse a lo ancho.
 */

const LABEL_W = 260;
const LEVEL_ROW_H = 28;
const MEASURE_ROW_H = 28;
const SUB_ROW_H = 24;
// «sin base» en cursiva no cabe en 64 px: partía la línea y la fila crecía.
const DELTA_W = 72;
/** Columna de representación (`% PVF`): `100,0 %` con holgura. */
const SHARE_W = 72;
const INDENT = 16;
/** Alto de la barra superior de Polaris (`3.5rem`), fija sobre el contenido. */
const TOPBAR_H = 56;
/** Tope de celdas pintadas. Por encima, se cortan filas y se avisa: pintar un millón de celdas cuelga la pestaña. */
const CELL_BUDGET = 250000;

// Los importes llevan la moneda en la celda (`$91,874,448`, `1.376 k€`).
const measureWidth = (kind, scaleKey) => {
  if (kind !== 'money') return 80;
  if (scaleKey === 'full') return 124;
  if (scaleKey === 'M') return 100;
  return 116;
};

/** Aplana el árbol de columnas: hojas y, tras los hijos de cada grupo, su subtotal. */
/**
 * Un grupo lleva columna de subtotal solo si tiene más de un hijo: con uno
 * («Básico › Básico») repetiría las mismas cifras.
 */
const hasSubtotal = (node) => Boolean(node.children && node.children.length > 1);

const flattenColumns = (root) => {
  const out = [];
  const walk = (node) => {
    node.children.forEach((child) => {
      if (child.children && child.children.length) {
        walk(child);
        if (hasSubtotal(child))
          out.push({ key: child.key, label: `Total ${child.label}`, subtotal: true, node: child });
      } else out.push({ key: child.key, label: child.label, subtotal: false, node: child });
    });
  };
  if (root) walk(root);
  return out;
};

/** Nº de columnas aplanadas bajo un nodo (incluido su subtotal si es grupo). */
const flatCount = (node) =>
  node.children && node.children.length
    ? node.children.reduce((a, c) => a + flatCount(c), 0) + (hasSubtotal(node) ? 1 : 0)
    : 1;

/** Filas de cabecera de nivel: celdas con colSpan/rowSpan en unidades de columna aplanada. */
const buildLevelRows = (root, levels) => {
  const rows = Array.from({ length: levels }, () => []);
  // `start`: índice de la primera columna aplanada que cubre la celda.
  let pos = 0;
  const walk = (node) => {
    node.children.forEach((child) => {
      const r = child.depth - 1;
      const only = child.children && child.children.length === 1 ? child.children[0] : null;
      if (only && !(only.children && only.children.length) && only.label === child.label) {
        // «Básico › Básico»: el hijo único repite la etiqueta. Una sola celda
        // que baja hasta la fila de medidas, como una hoja.
        rows[r].push({
          key: child.key,
          label: child.label,
          span: 1,
          rowSpan: levels - child.depth + 1,
          node: child,
          start: pos,
        });
        pos += 1;
      } else if (child.children && child.children.length) {
        rows[r].push({
          key: child.key,
          label: child.label,
          span: flatCount(child),
          rowSpan: 1,
          node: child,
          start: pos,
        });
        walk(child);
        if (hasSubtotal(child)) {
          rows[r + 1].push({
            key: `${child.key}__sub`,
            label: 'Total',
            span: 1,
            rowSpan: levels - child.depth,
            sub: true,
            start: pos,
          });
          pos += 1;
        }
      } else {
        rows[r].push({
          key: child.key,
          label: child.label,
          span: 1,
          rowSpan: levels - child.depth + 1,
          node: child,
          start: pos,
        });
        pos += 1;
      }
    });
  };
  if (root) walk(root);
  return rows;
};

const SortMark = ({ active, dir }) =>
  active ? (
    <span aria-hidden style={{ marginLeft: 3 }}>
      {dir === 'desc' ? '↓' : '↑'}
    </span>
  ) : null;

export const PivotTable = ({
  pivot,
  rowDims,
  colDims,
  measures,
  compare,
  fmt,
  dateLabels,
  moneyScale,
  pinTotal,
  sortBy,
  sortDir,
  onSort,
  loading,
  repr,
}) => {
  const scrollerRef = useRef(null);
  const headRef = useRef(null);
  const barRef = useRef(null);
  const subs = compare ? ['value', 'compare', 'delta'] : ['value'];

  // ---- Desplegado: nivel por defecto + nodos conmutados a mano. Se
  // reinicia al cambiar las dimensiones de filas (los nodos ya no son los mismos).
  const rowsSig = rowDims.join(':');
  const defaultLevel = Math.min(rowDims.length, 2);
  const [expand, setExpand] = useState({ sig: rowsSig, level: defaultLevel, toggled: new Set() });
  const exp = expand.sig === rowsSig ? expand : { sig: rowsSig, level: defaultLevel, toggled: new Set() };
  const isOpen = useCallback((node) => node.depth < exp.level !== exp.toggled.has(node.key), [exp.level, exp.toggled]);
  const toggle = (key) =>
    setExpand(() => {
      const t = new Set(exp.toggled);
      if (t.has(key)) t.delete(key);
      else t.add(key);
      return { sig: rowsSig, level: exp.level, toggled: t };
    });
  const setLevel = (level) => setExpand({ sig: rowsSig, level, toggled: new Set() });

  // ---- Columnas
  const flatCols = useMemo(() => flattenColumns(pivot?.columns), [pivot]);
  const levels = Math.max(1, colDims.length);
  const levelRows = useMemo(() => buildLevelRows(pivot?.columns, levels), [pivot, levels]);

  const unitCols = useMemo(
    () =>
      measures.flatMap((m) => {
        const w = measureWidth(MEASURE_BY_KEY[m].kind, moneyScale);
        const own = subs.map((s, si) => ({ m, s, w: s === 'delta' ? DELTA_W : w, first: si === 0 }));
        // Representación: una columna más a la derecha de su medida. Con
        // comparativa es una sola (el % de la fecha de valoración), no tres.
        return repr === m ? [...own, { m, s: 'share', w: SHARE_W, first: true }] : own;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [measures, moneyScale, compare, repr],
  );
  /**
   * Cabeceras de la fila de medidas: una por medida (abarca sus
   * subcolumnas) y una por la columna de %. `last` es el índice de su última
   * subcolumna, para el `right` cuando TOTAL va fijo.
   */
  const measureHeads = useMemo(() => {
    const out = [];
    unitCols.forEach((u, i) => {
      const kind = u.s === 'share' ? 'share' : 'measure';
      const prev = out[out.length - 1];
      if (prev && prev.m === u.m && prev.kind === kind) {
        prev.span += 1;
        prev.last = i;
      } else out.push({ m: u.m, kind, span: 1, first: i, last: i });
    });
    return out;
  }, [unitCols]);
  const unitW = unitCols.reduce((a, c) => a + c.w, 0);
  const totalW = unitW;
  const tableW = LABEL_W + flatCols.length * unitW + totalW;

  // `right` de cada subcolumna de TOTAL cuando va fija.
  const totalRight = useMemo(() => {
    const r = [];
    let acc = 0;
    for (let i = unitCols.length - 1; i >= 0; i -= 1) {
      r[i] = acc;
      acc += unitCols[i].w;
    }
    return r;
  }, [unitCols]);

  // ---- Filas visibles
  const { visibleRows, truncated } = useMemo(() => {
    const out = [];
    const walk = (nodes) => {
      nodes.forEach((n) => {
        const hasKids = n.children && n.children.length > 0;
        out.push({ node: n, hasKids });
        if (hasKids && isOpen(n)) walk(n.children);
      });
    };
    if (pivot?.rows?.children) walk(pivot.rows.children);
    const cellsPerRow = (flatCols.length + 1) * unitCols.length;
    const maxRows = Math.max(50, Math.floor(CELL_BUDGET / Math.max(1, cellsPerRow)));
    return { visibleRows: out.slice(0, maxRows), truncated: out.length > maxRows ? out.length : 0 };
  }, [pivot, isOpen, flatCols.length, unitCols.length]);

  // ---- Barra "Ir a" (grupos de primer nivel de columnas)
  const groups = useMemo(() => {
    if (!pivot?.columns?.children?.length || !colDims.length) return [];
    let x = 0;
    let idx = 0;
    return pivot.columns.children.map((g) => {
      const n = flatCount(g);
      const out = { key: g.key, label: g.label, x, from: idx, to: idx + n - 1 };
      x += n * unitW;
      idx += n;
      return out;
    });
  }, [pivot, colDims.length, unitW]);

  // Columnas donde empieza un grupo de primer nivel: llevan el separador
  // medio (LINE_GROUP) en cabecera y cuerpo.
  const groupStartIdx = useMemo(() => new Set(groups.map((g) => g.from)), [groups]);
  const groupStart = useMemo(
    () => new Set(groups.map((g) => flatCols[g.from]?.key).filter(Boolean)),
    [groups, flatCols],
  );

  const [view, setView] = useState({ from: 0, to: 0, overflow: false, width: 0, scrollWidth: 0 });
  const recomputeView = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const base = { overflow: el.scrollWidth > el.clientWidth + 1, width: el.clientWidth, scrollWidth: el.scrollWidth };
    if (!flatCols.length) {
      setView({ from: 0, to: 0, ...base });
      return;
    }
    const left = el.scrollLeft;
    const usable = el.clientWidth - LABEL_W - (pinTotal ? totalW : 0);
    const from = Math.min(flatCols.length - 1, Math.floor(left / unitW));
    const to = Math.min(flatCols.length - 1, Math.max(from, Math.floor((left + usable) / unitW) - 1));
    setView({ from, to, ...base });
  }, [flatCols.length, unitW, pinTotal, totalW]);

  // Tres piezas desplazan en horizontal a la vez: el cuerpo (manda), la
  // cabecera y la barra del pie. Asignar el mismo `scrollLeft` no vuelve a
  // disparar `scroll`, así que no hay bucle.
  const onBodyScroll = () => {
    const left = scrollerRef.current?.scrollLeft ?? 0;
    if (headRef.current && headRef.current.scrollLeft !== left) headRef.current.scrollLeft = left;
    if (barRef.current && barRef.current.scrollLeft !== left) barRef.current.scrollLeft = left;
    recomputeView();
  };
  const onBarScroll = () => {
    const el = scrollerRef.current;
    if (el && barRef.current && el.scrollLeft !== barRef.current.scrollLeft) el.scrollLeft = barRef.current.scrollLeft;
  };
  // La cabecera no tiene barra propia: la rueda horizontal sobre ella mueve el cuerpo.
  const onHeadWheel = (e) => {
    if (scrollerRef.current && Math.abs(e.deltaX) > Math.abs(e.deltaY)) scrollerRef.current.scrollLeft += e.deltaX;
  };
  useEffect(() => {
    recomputeView();
    window.addEventListener('resize', recomputeView);
    return () => window.removeEventListener('resize', recomputeView);
  }, [recomputeView, pivot]);

  const jumpTo = (x) => scrollerRef.current?.scrollTo({ left: x, behavior: 'smooth' });
  const page = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const usable = el.clientWidth - LABEL_W - (pinTotal ? totalW : 0);
    el.scrollBy({ left: dir * Math.max(unitW, usable - unitW / 2), behavior: 'smooth' });
  };

  if (!pivot) return null;

  const headerTops = [];
  for (let i = 0; i < levels; i += 1) headerTops.push(i * LEVEL_ROW_H);
  const measureTop = levels * LEVEL_ROW_H;
  const subTop = measureTop + MEASURE_ROW_H;

  const th = (extra) => ({
    position: 'sticky',
    background: HEADER_BG,
    borderBottom: `1px solid ${LINE}`,
    fontSize: 12,
    fontWeight: 600,
    color: INK,
    padding: '0 8px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    zIndex: 2,
    ...extra,
  });
  const pinnedTotal = (i, extra = {}) =>
    pinTotal ? { position: 'sticky', right: totalRight[i], zIndex: 1, ...extra } : extra;

  const measureLabel = (m) => {
    const def = MEASURE_BY_KEY[m];
    if (m === 'families') return `${def.label}¹`;
    return def.label;
  };

  const renderCells = (node, colKey, isTotalCol, rowKind) =>
    unitCols.map((u, i) => {
      const def = MEASURE_BY_KEY[u.m];
      const c = node.cells?.[colKey]?.[u.m];
      let text = '';
      let color = INK;
      let italic = false;
      if (c) {
        if (u.s === 'value') text = fmt.cell(def.kind, c.value);
        else if (u.s === 'share') {
          // Representación: parte del TOTAL de su columna (la fila TOTAL es
          // el 100 %); en la columna TOTAL, del gran total. Misma cuenta.
          text = fmt.share(c.value, pivot.rows.cells?.[colKey]?.[u.m]?.value);
          color = INK_SOFT;
        } else if (u.s === 'compare') {
          text = fmt.cell(def.kind, c.compare);
          color = INK_SOFT;
        } else {
          text = fmt.deltaPct(c);
          if (text === 'nueva' || text === 'sin base') {
            color = INK_SOFT;
            italic = true;
          }
        }
      }
      const firstOfMeasure = u.first;
      const sortedCell = isTotalCol && u.s === 'value' && sortBy === u.m;
      const style = {
        textAlign: 'right',
        padding: '0 8px',
        color,
        fontStyle: italic ? 'italic' : 'normal',
        fontWeight: sortedCell ? 700 : rowKind === 'leaf' ? 400 : 600,
        borderLeft:
          isTotalCol && i === 0
            ? `2px solid ${LINE_STRONG}`
            : i === 0 && groupStart.has(colKey)
              ? `1px solid ${LINE_GROUP}`
              : firstOfMeasure
                ? `1px solid ${LINE}`
                : 'none',
        ...(isTotalCol ? pinnedTotal(i) : {}),
      };
      return (
        // eslint-disable-next-line react/no-array-index-key
        <td key={`${colKey}-${i}`} style={style}>
          {text}
        </td>
      );
    });

  const renderRow = ({ node, hasKids }, kind) => {
    const open = hasKids && isOpen(node);
    const indent = 12 + Math.max(0, node.depth - 1) * INDENT;
    return (
      <tr key={node.key} className={`stk-row stk-${kind}`}>
        <td
          className="stk-label"
          style={{
            position: 'sticky',
            left: 0,
            zIndex: 1,
            paddingLeft: indent,
            paddingRight: 8,
            fontWeight: kind === 'leaf' ? 400 : 600,
            borderRight: `1px solid ${LINE}`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={node.label}
        >
          {hasKids ? (
            <button
              type="button"
              className="stk-toggle"
              aria-expanded={open}
              aria-label={open ? `Plegar ${node.label}` : `Desplegar ${node.label}`}
              onClick={() => toggle(node.key)}
            >
              <span aria-hidden style={{ display: 'inline-block', width: 14, color: INK_SOFT }}>
                {open ? '▾' : '▸'}
              </span>
              {node.label}
            </button>
          ) : (
            <span style={{ paddingLeft: rowDims.length > 1 ? 14 : 0 }}>{node.label}</span>
          )}
        </td>
        {flatCols.map((col) => renderCells(node, col.key, false, kind))}
        {renderCells(node, ROOT_KEY, true, kind)}
      </tr>
    );
  };

  const empty = !pivot.rows?.children?.length;
  const dimsLabel = rowDims.length ? rowDims.map((d) => DIMENSION_BY_KEY[d].label).join(' › ') : 'Total';

  const levelBtn = (active) => ({
    minWidth: 20,
    height: 20,
    padding: '0 5px',
    fontSize: 11,
    fontWeight: 600,
    color: INK,
    background: active ? '#E3E5E7' : '#FFFFFF',
    border: `1px solid ${LINE_STRONG}`,
    borderRadius: 4,
    cursor: 'pointer',
  });

  const tableClass = 'stk-table';
  const tableStyle = {
    width: view.width && tableW >= view.width * 0.6 && tableW < view.width ? '100%' : tableW,
    minWidth: tableW,
  };
  const colgroup = (
    <colgroup>
      <col style={{ width: LABEL_W }} />
      {flatCols.map((c) => unitCols.map((u, i) => <col key={`${c.key}-${i}`} style={{ width: u.w }} />))}
      {unitCols.map((u, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <col key={`T-${i}`} style={{ width: u.w }} />
      ))}
    </colgroup>
  );

  return (
    <div style={{ position: 'relative' }}>
      <style>{`
          .stk-card > .Polaris-Box { overflow: clip; }
      .stk-table { border-collapse: separate; border-spacing: 0; table-layout: fixed; font-size: 13px; color: ${INK}; font-variant-numeric: tabular-nums; }
          .stk-table td { height: 32px; white-space: nowrap; border-bottom: 1px solid ${LINE}; background: ${ROW_BG}; }
          .stk-row.stk-sub td { background: ${SUBTOTAL_BG}; }
          .stk-row.stk-grand td { background: ${GRAND_TOTAL_BG}; border-top: 2px solid ${INK_SOFT}; }
          .stk-row:hover td { background: ${ROW_HOVER}; }
          .stk-toggle { all: unset; cursor: pointer; display: inline-flex; align-items: center; max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
          .stk-toggle:focus-visible { outline: 2px solid ${LINK}; outline-offset: 2px; border-radius: 3px; }
          .stk-sort { all: unset; cursor: pointer; }
          .stk-sort:hover { color: ${PRIMARY}; }
          .stk-body { overflow-x: auto; overflow-y: hidden; scrollbar-width: none; }
          .stk-body::-webkit-scrollbar { display: none; }
          .stk-bar { position: sticky; bottom: 0; z-index: 3; overflow-x: auto; overflow-y: hidden; background: #fff; border-top: 1px solid ${LINE}; }
          .stk-bar::-webkit-scrollbar { height: 10px; }
          .stk-bar::-webkit-scrollbar-thumb { background: ${LINE_STRONG}; border-radius: 5px; border: 2px solid #fff; }
          .stk-bar::-webkit-scrollbar-track { background: #fff; }
          .stk-page { width: 22px; height: 22px; border: 1px solid ${LINE_STRONG}; border-radius: 6px; background: #fff; cursor: pointer; color: ${INK}; line-height: 18px; }
      `}</style>
      {/* Cabecera: "Ir a" y la cabecera de la tabla, pegadas bajo la barra superior. */}
      <div style={{ position: 'sticky', top: TOPBAR_H, zIndex: 4, background: '#fff' }}>
        {groups.length > 1 && view.overflow && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 16px',
              borderBottom: `1px solid ${LINE}`,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 12, color: INK_SOFT, marginRight: 2 }}>Ir a</span>
            {groups.map((g) => {
              const inView = g.to >= view.from && g.from <= view.to;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => jumpTo(g.x)}
                  style={{
                    fontSize: 12,
                    fontWeight: inView ? 600 : 400,
                    color: inView ? INK : INK_SOFT,
                    background: inView ? '#E3E5E7' : 'transparent',
                    border: 0,
                    borderRadius: 6,
                    padding: '2px 8px',
                    cursor: 'pointer',
                  }}
                >
                  {g.label}
                </button>
              );
            })}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: INK_SOFT }}>
              {`Columnas ${view.from + 1} a ${view.to + 1} de ${flatCols.length}, más TOTAL`}
            </span>
            <button type="button" className="stk-page" aria-label="Columnas anteriores" onClick={() => page(-1)}>
              ‹
            </button>
            <button type="button" className="stk-page" aria-label="Columnas siguientes" onClick={() => page(1)}>
              ›
            </button>
          </div>
        )}
        <div ref={headRef} onWheel={onHeadWheel} style={{ overflow: 'hidden' }}>
          {/* Si la tabla casi llena la tarjeta, se estira (las columnas crecen
              en proporción y TOTAL acaba en el borde). Si es mucho más
              estrecha (dos columnas), se queda a su ancho: estirarla daría
              columnas de cifras de 300 px. */}
          <table className={tableClass} style={tableStyle}>
            {colgroup}
            <thead>
              {levelRows.map((cells, li) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`lvl-${li}`}>
                  {li === 0 && (
                    <th
                      rowSpan={levels}
                      style={th({
                        top: 0,
                        left: 0,
                        zIndex: 4,
                        textAlign: 'left',
                        color: INK_SOFT,
                        borderRight: `1px solid ${LINE}`,
                        height: LEVEL_ROW_H * levels,
                      })}
                    >
                      <button
                        type="button"
                        className="stk-sort"
                        onClick={() => onSort('label')}
                        title="Ordenar por etiqueta"
                      >
                        {dimsLabel}
                        <SortMark active={sortBy === 'label'} dir={sortDir} />
                      </button>
                    </th>
                  )}
                  {cells.map((c) => (
                    <th
                      key={c.key}
                      colSpan={c.span * unitCols.length}
                      rowSpan={c.rowSpan}
                      title={c.label}
                      style={th({
                        top: headerTops[li],
                        height: LEVEL_ROW_H * c.rowSpan,
                        // Toda la cabecera de columnas va centrada (criterio de Aitor).
                        textAlign: 'center',
                        color: c.sub ? INK_SOFT : INK,
                        borderLeft: `1px solid ${groupStartIdx.has(c.start) ? LINE_GROUP : LINE}`,
                        // `clip` y no `hidden`: hidden crea contenedor de
                        // scroll y el sticky de dentro dejaría de funcionar.
                        overflow: 'clip',
                      })}
                    >
                      {/* La etiqueta del grupo se queda junto a la columna de
                          etiquetas mientras quede parte del grupo a la vista. */}
                      <span style={{ position: 'sticky', left: LABEL_W + 8, display: 'inline-block' }}>{c.label}</span>
                    </th>
                  ))}
                  {li === 0 && (
                    <th
                      colSpan={unitCols.length}
                      rowSpan={levels}
                      style={th({
                        top: 0,
                        height: LEVEL_ROW_H * levels,
                        fontWeight: 700,
                        textAlign: 'center',
                        borderLeft: `2px solid ${LINE_STRONG}`,
                        ...(pinTotal ? { right: 0, zIndex: 3 } : {}),
                      })}
                    >
                      TOTAL
                    </th>
                  )}
                </tr>
              ))}
              <tr>
                <th
                  rowSpan={compare ? 2 : 1}
                  style={th({
                    top: measureTop,
                    left: 0,
                    zIndex: 4,
                    textAlign: 'left',
                    fontWeight: 400,
                    color: INK_SOFT,
                    borderRight: `1px solid ${LINE}`,
                    height: MEASURE_ROW_H + (compare ? SUB_ROW_H : 0),
                  })}
                >
                  {rowDims.length > 1 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                      Desplegar a nivel
                      {rowDims.map((d, i) => (
                        <button
                          key={d}
                          type="button"
                          style={levelBtn(exp.level === i + 1 && exp.toggled.size === 0)}
                          onClick={() => setLevel(i + 1)}
                          title={`Mostrar hasta ${DIMENSION_BY_KEY[d].label}`}
                        >
                          {i + 1 === rowDims.length ? 'todo' : i + 1}
                        </button>
                      ))}
                    </span>
                  )}
                </th>
                {flatCols.map((c) =>
                  measureHeads.map((h, hi) => (
                    <th
                      key={`${c.key}-${h.m}-${h.kind}`}
                      colSpan={h.span}
                      rowSpan={h.kind === 'share' && compare ? 2 : 1}
                      style={th({
                        top: measureTop,
                        height: MEASURE_ROW_H + (h.kind === 'share' && compare ? SUB_ROW_H : 0),
                        textAlign: 'center',
                        color: h.kind === 'share' ? INK_SOFT : INK,
                        borderLeft: `1px solid ${hi === 0 && groupStart.has(c.key) ? LINE_GROUP : LINE}`,
                      })}
                    >
                      {h.kind === 'share' ? `% ${MEASURE_BY_KEY[h.m].label}` : measureLabel(h.m)}
                    </th>
                  )),
                )}
                {measureHeads.map((h, hi) => (
                  <th
                    key={`T-${h.m}-${h.kind}`}
                    colSpan={h.span}
                    rowSpan={h.kind === 'share' && compare ? 2 : 1}
                    style={th({
                      top: measureTop,
                      height: MEASURE_ROW_H + (h.kind === 'share' && compare ? SUB_ROW_H : 0),
                      textAlign: 'center',
                      color: h.kind === 'share' ? INK_SOFT : INK,
                      borderLeft: hi === 0 ? `2px solid ${LINE_STRONG}` : `1px solid ${LINE}`,
                      ...(pinTotal ? { right: totalRight[h.last], zIndex: 3 } : {}),
                    })}
                  >
                    {h.kind === 'share' ? (
                      `% ${MEASURE_BY_KEY[h.m].label}`
                    ) : (
                      <button
                        type="button"
                        className="stk-sort"
                        onClick={() => onSort(h.m)}
                        title={`Ordenar por ${MEASURE_BY_KEY[h.m].long} total`}
                      >
                        {measureLabel(h.m)}
                        {!compare && <SortMark active={sortBy === h.m} dir={sortDir} />}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
              {compare && (
                <tr>
                  {[...flatCols.map((c) => ({ key: c.key, total: false })), { key: ROOT_KEY, total: true }].map((c) =>
                    unitCols.map((u, i) => {
                      // La columna de % ya ocupa esta fila con su cabecera (rowSpan 2).
                      if (u.s === 'share') return null;
                      const label =
                        u.s === 'value' ? dateLabels.current : u.s === 'compare' ? dateLabels.compare : 'Δ %';
                      const sorted = c.total && u.s === 'value' && sortBy === u.m;
                      return (
                        <th
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${c.key}-s-${i}`}
                          style={th({
                            top: subTop,
                            height: SUB_ROW_H,
                            padding: '0 6px',
                            textAlign: 'center',
                            fontWeight: sorted ? 700 : 500,
                            color: u.s === 'compare' ? INK_FAINT : INK_SOFT,
                            borderLeft:
                              c.total && i === 0
                                ? `2px solid ${LINE_STRONG}`
                                : i === 0 && !c.total && groupStart.has(c.key)
                                  ? `1px solid ${LINE_GROUP}`
                                  : u.first
                                    ? `1px solid ${LINE}`
                                    : 'none',
                            ...(c.total && pinTotal ? { right: totalRight[i], zIndex: 3 } : {}),
                          })}
                        >
                          {label}
                          <SortMark active={sorted} dir={sortDir} />
                        </th>
                      );
                    }),
                  )}
                </tr>
              )}
            </thead>
          </table>
        </div>
      </div>

      <div ref={scrollerRef} onScroll={onBodyScroll} className="stk-body">
        <table className={tableClass} style={tableStyle}>
          {colgroup}
          <tbody>
            {visibleRows.map((r) => renderRow(r, r.hasKids ? 'sub' : 'leaf'))}
            {renderRow({ node: pivot.rows, hasKids: false }, 'grand')}
          </tbody>
        </table>
        {empty && (
          <div style={{ padding: 24, textAlign: 'center', color: INK_SOFT, fontSize: 13 }}>
            No hay existencias con estas fechas y filtros.
          </div>
        )}
      </div>
      {view.overflow && (
        <div ref={barRef} onScroll={onBarScroll} className="stk-bar" aria-hidden>
          <div style={{ width: view.scrollWidth, height: 1 }} />
        </div>
      )}
      {loading && (
        <div
          aria-live="polite"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(246, 246, 247, 0.65)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 80,
            zIndex: 5,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: '#fff',
              border: `1px solid ${LINE}`,
              borderRadius: 8,
              padding: '8px 14px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              fontSize: 13,
              color: INK,
            }}
          >
            <Spinner size="small" accessibilityLabel="Calculando" />
            <span aria-hidden>Calculando…</span>
          </div>
        </div>
      )}
      {truncated > 0 && (
        <div style={{ padding: '8px 16px', fontSize: 12, color: '#916A00' }}>
          {`Se muestran ${visibleRows.length} de ${truncated} filas desplegadas. Pliega niveles o filtra para ver el resto; el Excel las trae todas.`}
        </div>
      )}
    </div>
  );
};
